import React from "react";
import {
  listArtistVariations,
  reconcileDecision,
  renameArtist,
  reviewVariation,
} from "../api/artistReview";

// The existing store is hook-local. No review pages survive filter changes or
// unmounts. A generation invalidates GETs even when the transport ignores abort.
export default function useArtistReview() {
  const [filter, setFilter] = React.useState("unreviewed");
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [pending, setPending] = React.useState({});
  const [rowErrors, setRowErrors] = React.useState({});
  const [revision, setRevision] = React.useState(0);
  const [announcement, setAnnouncement] = React.useState("");
  const [denied, setDenied] = React.useState(false);
  const generation = React.useRef(0);
  const active = React.useRef(new Set());
  const alive = React.useRef(true);
  const controller = React.useRef(null);
  const needsRefill = React.useRef(false);
  const hasPending = Object.keys(pending).length > 0;

  const invalidate = React.useCallback(() => {
    generation.current += 1;
    controller.current?.abort();
  }, []);

  const refresh = React.useCallback(() => {
    invalidate();
    if (active.current.size) {
      needsRefill.current = true;
      return;
    }
    setRevision((value) => value + 1);
  }, [invalidate]);

  React.useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      invalidate();
    };
  }, [invalidate]);

  React.useEffect(() => {
    if (active.current.size || denied) return;
    const request = ++generation.current;
    const abort = new AbortController();
    controller.current = abort;
    setLoading(true);
    setError(null);
    listArtistVariations({ filter, page, search, signal: abort.signal })
      .then((result) => {
        if (request === generation.current) setData(result);
      })
      .catch((failure) => {
        if (request !== generation.current || failure.name === "AbortError")
          return;
        if (
          failure.status === 401 ||
          (failure.status === 403 && failure.message === "Permission denied")
        ) {
          setDenied(true);
          setData(null);
        }
        setError(failure);
      })
      .finally(() => {
        if (request === generation.current) setLoading(false);
      });
    return () => abort.abort();
  }, [filter, page, search, revision, denied]);

  const changeView = (update) => {
    if (active.current.size) return;
    invalidate();
    update();
    setData(null);
    setLoading(true);
    setError(null);
    setRowErrors({});
    setAnnouncement("");
  };

  const mutate = async (key, operation, reconcile, message) => {
    if (active.current.has(key) || denied) return false;
    active.current.add(key);
    invalidate();
    setLoading(false);
    setPending((previous) => ({ ...previous, [key]: true }));
    setRowErrors((previous) => ({ ...previous, [key]: null }));
    try {
      const result = await operation();
      if (!alive.current) return false;
      setData((previous) => reconcile(previous, result));
      setAnnouncement(message(result));
      needsRefill.current = true;
      return true;
    } catch (failure) {
      if (!alive.current) return false;
      setRowErrors((previous) => ({ ...previous, [key]: failure }));
      if (failure.status === 404) needsRefill.current = true;
      if (
        failure.status === 401 ||
        (failure.status === 403 && failure.message === "Permission denied")
      ) {
        setDenied(true);
        setData(null);
      }
      return false;
    } finally {
      active.current.delete(key);
      if (alive.current) {
        setPending((previous) => {
          const next = { ...previous };
          delete next[key];
          return next;
        });
        if (!active.current.size && needsRefill.current) {
          needsRefill.current = false;
          setPage(1);
          setRevision((value) => value + 1);
        }
      }
    }
  };

  return {
    filter,
    page,
    search,
    data,
    loading,
    error,
    pending,
    rowErrors,
    announcement,
    denied,
    hasPending,
    refresh,
    changeFilter: (next) => {
      if (next === filter) return;
      changeView(() => {
        setFilter(next);
        setPage(1);
      });
    },
    changePage: (next) => {
      if (next === page) return;
      changeView(() => setPage(next));
    },
    changeSearch: (value) => {
      const next = value.trim() ? value : "";
      if (next === search) return;
      changeView(() => {
        setSearch(next);
        setPage(1);
      });
    },
    decide: (artistId, variationId, decision) =>
      mutate(
        `variation:${variationId}`,
        () => reviewVariation(variationId, artistId, decision),
        (previous, result) => reconcileDecision(previous, result, filter),
        (result) => `${result.name} ${result.status}.`,
      ),
    rename: (artistId, name) =>
      mutate(
        `artist:${artistId}`,
        () => renameArtist(artistId, name),
        (previous, result) =>
          previous && {
            ...previous,
            artists: previous.artists.map((artist) =>
              artist.id === result.id
                ? { ...artist, name: result.name }
                : artist,
            ),
          },
        (result) => `Primary name saved: ${result.name}.`,
      ),
  };
}
